/** Базовые планы (ПЛ-16): до 10 версий, основной, сравнение двух любых версий; утверждение графика */
import type { BaselineCompare, ChangeSummary, ScheduleView } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime, tenge } from '../../lib/format';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { DelaysPanel } from './DelaysPanel';
import { LField } from './LField';
import { fmtDur, fmtMoment, hpd } from './model';

export function BaselinesPanel({ view, onView }: { view: ScheduleView; onView: (v: ScheduleView) => void }) {
  const [name, setName] = useState('');
  const [reopen, setReopen] = useState(false);
  const primary = view.baselines.find((b) => b.isPrimary);
  const [a, setA] = useState<string>(primary?.id ?? view.baselines[0]?.id ?? 'current');
  const [b, setB] = useState<string>('current');
  const act = useMutation({
    mutationFn: (x: { url: string; method?: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<ScheduleView>(x.url, { method: x.method ?? 'POST', body: x.body }),
    onSuccess: (v) => {
      onView(v);
      toast('Готово');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const cmp = useQuery({
    queryKey: ['schedule-compare', view.schedule.id, a, b, view.schedule.revision, view.baselines.length],
    queryFn: () => api<BaselineCompare>(`/schedules/${view.schedule.id}/compare`, { query: { a, b } }),
    enabled: a !== b,
  });
  const approved = view.schedule.status === 'approved';
  const h = hpd(view);
  const showCost = Boolean(cmp.data?.rows.some((r) => r.costA != null || r.costB != null));
  const [onlyChanged, setOnlyChanged] = useState(true);
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Базовые планы"
        meta={`${view.baselines.length} из 10`}
        actions={
          view.can.baseline ? (
            approved ? (
              <Button onClick={() => setReopen(true)} className="max-md:h-11">
                Вернуть в работу
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (
                    window.confirm(
                      'Утвердить график? Будет сохранён базовый план «Утверждённый график», дальше сроки и логика меняются через запрос на изменение.',
                    )
                  )
                    act.mutate({ url: `/schedules/${view.schedule.id}/approve` });
                }}
                className="max-md:h-11"
              >
                Утвердить график
              </Button>
            )
          ) : null
        }
      >
        {view.schedule.reopen ? (
          <p className="mb-3 text-[13px] text-ink-2">
            Последний возврат в работу {dateTime(view.schedule.reopen.at)}: {view.schedule.reopen.reason}
            {view.schedule.reopen.change ? (
              <>
                {' '}
                — по запросу{' '}
                <Link
                  to="/changes/$changeId"
                  params={{ changeId: view.schedule.reopen.change.id }}
                  className="text-ink hover:underline"
                >
                  {view.schedule.reopen.change.number} {view.schedule.reopen.change.title}
                </Link>
              </>
            ) : null}
          </p>
        ) : null}
        {view.can.baseline ? (
          <form
            className="mb-4 flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              act.mutate({ url: `/schedules/${view.schedule.id}/baselines`, body: { name: name.trim() } });
              setName('');
            }}
          >
            <LField label="Название нового базового плана" className="min-w-[220px] flex-1">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например, «Договорный график»"
                maxLength={200}
                className="max-md:h-11"
              />
            </LField>
            <Button
              variant="primary"
              type="submit"
              loading={act.isPending}
              disabled={view.baselines.length >= 10}
              className="max-md:h-11"
            >
              Сохранить базовый план
            </Button>
          </form>
        ) : null}
        {view.baselines.length ? (
          <ul className="divide-y divide-line">
            {view.baselines.map((x) => (
              <li key={x.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5">
                <span className="min-w-0 flex-1">
                  <b>БП-{x.number}</b> {x.name} {x.isPrimary ? <Badge tone="accent">основной</Badge> : null}
                  <span className="block text-[13px] text-ink-2">
                    {fmtMoment(x.start)} — {fmtMoment(x.finish)} · {x.activityCount} работ · сохранён{' '}
                    {dateTime(x.createdAt)}
                  </span>
                </span>
                {view.can.baseline ? (
                  <span className="flex gap-1">
                    {!x.isPrimary ? (
                      <IconButton
                        label="Сделать основным"
                        onClick={() =>
                          act.mutate({
                            url: `/baselines/${x.id}`,
                            method: 'PATCH',
                            body: { isPrimary: true },
                          })
                        }
                        className="size-11 md:size-10"
                      >
                        <Star className="size-4" />
                      </IconButton>
                    ) : null}
                    <IconButton
                      label="Удалить базовый план"
                      onClick={() =>
                        window.confirm(`Удалить БП-${x.number} «${x.name}»?`) &&
                        act.mutate({ url: `/baselines/${x.id}`, method: 'DELETE' })
                      }
                      className="size-11 md:size-10"
                    >
                      <Trash2 className="size-4" />
                    </IconButton>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="Базовых планов пока нет">
            Сохраните базовый план, чтобы сравнивать с ним факт и прогноз.
          </EmptyState>
        )}
      </Panel>

      <Panel title="Сравнение версий" meta="по датам, длительностям и составу работ">
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <LField label="Версия А">
            <Select value={a} onChange={(e) => setA(e.target.value)} className="max-md:h-11">
              <option value="current">Текущий график</option>
              {view.baselines.map((x) => (
                <option key={x.id} value={x.id}>
                  БП-{x.number}: {x.name}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Версия Б">
            <Select value={b} onChange={(e) => setB(e.target.value)} className="max-md:h-11">
              <option value="current">Текущий график</option>
              {view.baselines.map((x) => (
                <option key={x.id} value={x.id}>
                  БП-{x.number}: {x.name}
                </option>
              ))}
            </Select>
          </LField>
          <label className="flex min-h-11 items-center gap-2 text-[14px]">
            <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />{' '}
            Только изменения
          </label>
        </div>
        {a === b ? (
          <p className="text-[14px] text-ink-2">Выберите две разные версии.</p>
        ) : cmp.isPending ? (
          <Spinner />
        ) : cmp.isError ? (
          <ErrorBox error={cmp.error} />
        ) : (
          <>
            <p className="mb-3 text-[14px]">
              Окончание: {fmtMoment(cmp.data.a.finish)} → {fmtMoment(cmp.data.b.finish)}
              {cmp.data.totals.finishShiftDays != null ? (
                <b
                  className={clsx(
                    'ml-2',
                    cmp.data.totals.finishShiftDays > 0 ? 'text-danger' : 'text-success',
                  )}
                >
                  {cmp.data.totals.finishShiftDays > 0 ? '+' : ''}
                  {String(cmp.data.totals.finishShiftDays).replace('.', ',')} раб. дн
                </b>
              ) : null}
              <span className="ml-3 text-ink-2">
                изменено {cmp.data.totals.changed}, добавлено {cmp.data.totals.added}, удалено{' '}
                {cmp.data.totals.removed}
              </span>
            </p>
            {cmp.data.rows.some((r) => r.costA != null || r.costB != null) ? (
              <p className="mb-3 text-[14px]">
                Стоимость по назначениям ресурсов:{' '}
                {tenge(cmp.data.rows.reduce((n, r) => n + (r.costA ?? 0), 0))} →{' '}
                {tenge(cmp.data.rows.reduce((n, r) => n + (r.costB ?? 0), 0))}
              </p>
            ) : null}
            <ScrollX>
              <table className="w-full min-w-[760px] text-[13px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2 pr-2 pl-3">Работа</th>
                    <th className="py-2 pr-2">Начало А → Б</th>
                    <th className="py-2 pr-2">Окончание А → Б</th>
                    <th className="py-2 pr-2">Длительность А → Б</th>
                    {showCost ? <th className="py-2 pr-2 text-right">Стоимость А → Б</th> : null}
                    <th className="py-2 pr-3 text-right">Сдвиг</th>
                  </tr>
                </thead>
                <tbody>
                  {cmp.data.rows
                    .filter((r) => !onlyChanged || r.change !== 'same')
                    .slice(0, 1000)
                    .map((r) => (
                      <tr key={r.activityId} className="border-t border-line">
                        <td className="py-1.5 pr-2 pl-3">
                          {r.code} {r.name}
                          {r.change === 'added' ? (
                            <Badge tone="success" className="ml-1">
                              новая
                            </Badge>
                          ) : r.change === 'removed' ? (
                            <Badge tone="danger" className="ml-1">
                              удалена
                            </Badge>
                          ) : null}
                        </td>
                        <td className="num py-1.5 pr-2">
                          {fmtMoment(r.startA)} → {fmtMoment(r.startB)}
                        </td>
                        <td className="num py-1.5 pr-2">
                          {fmtMoment(r.finishA)} → {fmtMoment(r.finishB)}
                        </td>
                        <td className="num py-1.5 pr-2">
                          {fmtDur(r.durationA, h)} → {fmtDur(r.durationB, h)}
                        </td>
                        {showCost ? (
                          <td
                            className={clsx(
                              'num py-1.5 pr-2 text-right',
                              (r.costB ?? 0) > (r.costA ?? 0) && 'text-danger',
                            )}
                          >
                            {r.costA != null ? tenge(r.costA) : '—'} →{' '}
                            {r.costB != null ? tenge(r.costB) : '—'}
                          </td>
                        ) : null}
                        <td
                          className={clsx(
                            'num py-1.5 pr-3 text-right',
                            r.finishShiftDays && r.finishShiftDays > 0 && 'text-danger',
                          )}
                        >
                          {r.finishShiftDays == null
                            ? '—'
                            : `${r.finishShiftDays > 0 ? '+' : ''}${String(r.finishShiftDays).replace('.', ',')}`}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </ScrollX>
          </>
        )}
      </Panel>
      <DelaysPanel view={view} />
      {reopen ? (
        <ReopenDialog
          projectId={view.schedule.projectId}
          required={view.schedule.settings.reopenByChange}
          onClose={() => setReopen(false)}
          onSubmit={(reason, changeId) => {
            act.mutate({ url: `/schedules/${view.schedule.id}/reopen`, body: { reason, changeId } });
            setReopen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function ReopenDialog({
  projectId,
  required,
  onClose,
  onSubmit,
}: {
  projectId: string;
  required: boolean;
  onClose: () => void;
  onSubmit: (reason: string, changeId: string | null) => void;
}) {
  const [reason, setReason] = useState('');
  const [changeId, setChangeId] = useState('');
  // Утверждённые запросы на изменение проекта (раздел 6.11); без права их видеть — пустой список
  const changes = useQuery({
    queryKey: ['approved-changes', projectId],
    queryFn: () => api<ChangeSummary[]>('/changes', { query: { projectId, status: 'approved' } }),
    retry: false,
  });
  const list = (changes.data ?? []).filter((c) => c.status === 'approved' || c.status === 'implemented');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Вернуть график в работу"
      description="Причина изменения утверждённого графика сохраняется; после правок график утверждается снова с новым базовым планом."
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={reason.trim().length < 5 || (required && !changeId)}
            onClick={() => onSubmit(reason.trim(), changeId || null)}
            className="max-md:h-11"
          >
            Вернуть в работу
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <LField
          label="Запрос на изменение"
          hint={
            required
              ? 'Обязательно по настройке графика'
              : 'Основание изменения сроков — из раздела «Изменения»'
          }
        >
          <Select value={changeId} onChange={(e) => setChangeId(e.target.value)} className="max-md:h-11">
            <option value="">
              {list.length ? 'Выберите утверждённый запрос…' : 'Утверждённых запросов нет'}
            </option>
            {list.map((c) => (
              <option key={c.id} value={c.id}>
                {c.number} {c.title}
                {c.daysDelta ? ` (${c.daysDelta > 0 ? '+' : ''}${c.daysDelta} дн)` : ''}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Причина изменения">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} />
        </LField>
      </div>
    </Dialog>
  );
}
